import { useEffect, useState } from 'react';
import type { Room, RoomRole, RoomStatus } from '../../lib/rooms';
import { ConfirmRow } from '../ConfirmRow';
import { StatusPill } from './StatusPill';

interface HostRailStatusProps {
  room: Room;
  myRole: RoomRole;
  onSetStatus: (status: RoomStatus) => void;
  onMarkStreamStart: () => void;
  pending: boolean;
}

interface Transition {
  status: RoomStatus;
  label: string;
  hostOnly: boolean;
  /** Confirmation copy — `null` means the transition fires immediately, no confirm step. */
  confirm: string | null;
}

/** DRAFT→LIVE→FINISHED→ARCHIVED, with FINISHED→LIVE ("reopen") open to the host only. Finishing
 * and archiving both need a confirm — per `docs/specs/WP8-exports.md` — because either one changes
 * who can edit the table; going live or reopening does not. */
const NEXT: Record<RoomStatus, Transition | null> = {
  DRAFT: { status: 'LIVE', label: 'Go live', hostOnly: false, confirm: null },
  LIVE: {
    status: 'FINISHED',
    label: 'Finish interview',
    hostOnly: false,
    confirm: 'Finish this interview? The table becomes read-only for everyone but the host.',
  },
  FINISHED: { status: 'LIVE', label: 'Reopen', hostOnly: true, confirm: null },
  ARCHIVED: null,
};

const ARCHIVE_CONFIRM = 'Archive this room? It leaves the Home list but stays reachable by its URL.';

function elapsedSince(streamStartedAt: string, now: number): string {
  const seconds = Math.max(0, Math.floor((now - new Date(streamStartedAt).getTime()) / 1000));
  const minutes = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const remainder = (seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${remainder}`;
}

/** Status pill + transition button(s) + "mark stream start" + elapsed timer. The timer ticks every
 * second while the room is LIVE (and stream start is set), and stops — clearing its interval — the
 * moment the room leaves LIVE or this rail unmounts. Finish and Archive arm an inline confirm
 * (`rawArmed`) instead of firing immediately; it's tagged with the `RoomStatus` it was armed for,
 * so a status change from outside (the broadcast a successful mutation triggers, or another tab)
 * disarms it for free — derived during render rather than reset from an effect. */
export function HostRailStatus({ room, myRole, onSetStatus, onMarkStreamStart, pending }: HostRailStatusProps) {
  const isHost = myRole === 'HOST';
  const next = NEXT[room.status];
  const canAdvance = next !== null && (!next.hostOnly || isHost);
  const [now, setNow] = useState(() => Date.now());
  const [rawArmed, setRawArmed] = useState<{ kind: 'advance' | 'archive'; status: RoomStatus } | null>(null);
  const armed = rawArmed !== null && rawArmed.status === room.status ? rawArmed.kind : null;
  const isTicking = room.status === 'LIVE' && room.streamStartedAt !== null;

  function setArmed(kind: 'advance' | 'archive' | null) {
    setRawArmed(kind === null ? null : { kind, status: room.status });
  }

  useEffect(() => {
    if (!isTicking) {
      return;
    }
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isTicking]);

  const elapsed = room.streamStartedAt ? elapsedSince(room.streamStartedAt, now) : null;

  function advance() {
    if (!next) {
      return;
    }
    if (next.confirm && armed !== 'advance') {
      setArmed('advance');
      return;
    }
    onSetStatus(next.status);
    setArmed(null);
  }

  function archive() {
    if (armed !== 'archive') {
      setArmed('archive');
      return;
    }
    onSetStatus('ARCHIVED');
    setArmed(null);
  }

  return (
    <section>
      <div className="flex items-baseline gap-4">
        <span className="eyebrow">02 / Status</span>
        <span className="h-px flex-1 bg-border" aria-hidden="true" />
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <StatusPill status={room.status} />
        {elapsed && <span className="telemetry text-text-muted">{elapsed}</span>}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {canAdvance && next && armed === 'advance' && next.confirm ? (
          <ConfirmRow
            message={next.confirm}
            pending={pending}
            confirmLabel={next.label}
            onConfirm={advance}
            onCancel={() => setArmed(null)}
          />
        ) : (
          canAdvance &&
          next && (
            <button
              type="button"
              disabled={pending}
              onClick={advance}
              className="min-h-tap flex-1 rounded-md border border-accent-border bg-accent-dim px-3 text-sm font-semibold text-accent disabled:opacity-50"
            >
              {next.label}
            </button>
          )
        )}

        {isHost && room.status === 'FINISHED' && (
          armed === 'archive' ? (
            <ConfirmRow
              message={ARCHIVE_CONFIRM}
              pending={pending}
              confirmLabel="Archive"
              onConfirm={archive}
              onCancel={() => setArmed(null)}
            />
          ) : (
            <button
              type="button"
              disabled={pending}
              onClick={archive}
              className="min-h-tap flex-1 rounded-md border border-border-strong px-3 text-sm font-semibold text-text-muted hover:border-signal hover:text-signal disabled:opacity-50"
            >
              Archive
            </button>
          )
        )}

        {!room.streamStartedAt && (
          <button
            type="button"
            disabled={pending}
            onClick={onMarkStreamStart}
            className="min-h-tap w-full rounded-md border border-border-strong px-3 text-sm font-semibold text-text-muted hover:border-accent-border hover:text-text disabled:opacity-50"
          >
            Mark stream start
          </button>
        )}
      </div>
    </section>
  );
}
